import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Container, Box, Paper, Typography, Button, TextField, IconButton,
  FormControl, InputLabel, Select, MenuItem, Table, TableHead, TableBody,
  TableRow, TableCell, Checkbox, Switch, Dialog, DialogTitle, DialogContent,
  DialogActions, Alert, useMediaQuery, useTheme,
} from '@mui/material';
import { ChecklistRtl, Add, Edit, SyncAlt } from '@mui/icons-material';
import { MenuInicial } from './MenuInicial';
import {
  API_BASE_URL, SUCURSALES_LISTAR, CHECKLIST_TAREAS_LISTAR, CHECKLIST_TAREA_ALTA,
  CHECKLIST_TAREA_ACTUALIZAR, CHECKLIST_ASIGNACION_TOGGLE, CHECKLIST_TAREA_CLONAR,
} from '../services/apiConfig';
import TituloPagina from './utils/TituloPagina.jsx';
import Cargando from './utils/Cargando.jsx';
import { avisarExito, avisarError } from '../services/toast';
import axios from 'axios';

const authHeader = () => {
  const t = sessionStorage.getItem('authToken');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const DIAS = [
  { n: 1, label: 'Lunes' }, { n: 2, label: 'Martes' }, { n: 3, label: 'Miércoles' },
  { n: 4, label: 'Jueves' }, { n: 5, label: 'Viernes' }, { n: 6, label: 'Sábado' }, { n: 7, label: 'Domingo' },
];
const TURNOS = [1, 2];
const clave = (dia, turno) => `${dia}-${turno}`;

function ChecklistTareas() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [sucursales, setSucursales] = useState([]);
  const [sucursalId, setSucursalId] = useState('');
  const [loading, setLoading] = useState(true);
  const [tareas, setTareas] = useState([]); // [{id, descripcion, orden, activo, asignado: Set('dia-turno')}]
  const [error, setError] = useState('');

  const [nuevaOpen, setNuevaOpen] = useState(false);
  const [nuevaDescripcion, setNuevaDescripcion] = useState('');
  const [guardandoNueva, setGuardandoNueva] = useState(false);

  const [editar, setEditar] = useState(null); // tarea o null
  const [editDescripcion, setEditDescripcion] = useState('');
  const [guardandoEdit, setGuardandoEdit] = useState(false);

  const [clonarOpen, setClonarOpen] = useState(false);
  const [clonarDestino, setClonarDestino] = useState('');
  const [clonando, setClonando] = useState(false);

  useEffect(() => {
    axios.get(API_BASE_URL + SUCURSALES_LISTAR, { headers: authHeader() })
      .then((res) => {
        const lista = (Array.isArray(res.data?.data) ? res.data.data : []).filter((s) => Number(s.activo) === 1);
        setSucursales(lista);
        if (lista.length) setSucursalId(lista[0].id);
      })
      .catch(() => setError('Error al cargar sucursales.'));
  }, []);

  const agrupar = (filas) => {
    const mapa = new Map();
    filas.forEach((f) => {
      if (!mapa.has(f.id)) {
        mapa.set(f.id, { id: f.id, descripcion: f.descripcion, orden: f.orden, activo: Number(f.activo) === 1, asignado: new Set() });
      }
      if (f.dia_semana != null && f.turno_numero != null) {
        mapa.get(f.id).asignado.add(clave(f.dia_semana, f.turno_numero));
      }
    });
    return Array.from(mapa.values());
  };

  const cargarTareas = useCallback((sid) => {
    if (!sid) return;
    setLoading(true);
    axios.get(API_BASE_URL + CHECKLIST_TAREAS_LISTAR, { params: { sucursal_id: sid }, headers: authHeader() })
      .then((res) => setTareas(agrupar(Array.isArray(res.data?.data) ? res.data.data : [])))
      .catch((e) => setError(e.response?.data?.message || 'Error al cargar las tareas.'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { if (sucursalId) cargarTareas(sucursalId); }, [sucursalId, cargarTareas]);

  const crearTarea = async () => {
    if (!nuevaDescripcion.trim()) return;
    setGuardandoNueva(true);
    try {
      const res = await axios.post(API_BASE_URL + CHECKLIST_TAREA_ALTA, {
        sucursal_id: sucursalId, descripcion: nuevaDescripcion.trim(),
      }, { headers: authHeader() });
      if (!res.data?.success) throw new Error(res.data?.message || 'Error al crear la tarea.');
      avisarExito('Tarea creada.');
      setNuevaOpen(false);
      setNuevaDescripcion('');
      cargarTareas(sucursalId);
    } catch (e) {
      avisarError(e.response?.data?.message || e.message || 'Error al crear la tarea.');
    } finally {
      setGuardandoNueva(false);
    }
  };

  const abrirEditar = (t) => { setEditar(t); setEditDescripcion(t.descripcion); };

  const guardarEdicion = async () => {
    if (!editDescripcion.trim()) return;
    setGuardandoEdit(true);
    try {
      const res = await axios.post(API_BASE_URL + CHECKLIST_TAREA_ACTUALIZAR, {
        id: editar.id, descripcion: editDescripcion.trim(), activo: editar.activo ? 1 : 0,
      }, { headers: authHeader() });
      if (!res.data?.success) throw new Error(res.data?.message || 'Error al guardar.');
      avisarExito('Tarea actualizada.');
      setEditar(null);
      cargarTareas(sucursalId);
    } catch (e) {
      avisarError(e.response?.data?.message || e.message || 'Error al guardar.');
    } finally {
      setGuardandoEdit(false);
    }
  };

  const toggleActivo = (t) => {
    const nuevoActivo = !t.activo;
    setTareas((prev) => prev.map((x) => x.id === t.id ? { ...x, activo: nuevoActivo } : x));
    axios.post(API_BASE_URL + CHECKLIST_TAREA_ACTUALIZAR, {
      id: t.id, descripcion: t.descripcion, activo: nuevoActivo ? 1 : 0,
    }, { headers: authHeader() })
      .then((res) => { if (!res.data?.success) throw new Error(res.data?.message); })
      .catch((e) => {
        setTareas((prev) => prev.map((x) => x.id === t.id ? { ...x, activo: t.activo } : x));
        avisarError(e.response?.data?.message || e.message || 'Error al cambiar el estado.');
      });
  };

  const toggleCelda = (t, dia, turno) => {
    const k = clave(dia, turno);
    const estabaAsignado = t.asignado.has(k);
    setTareas((prev) => prev.map((x) => {
      if (x.id !== t.id) return x;
      const asignado = new Set(x.asignado);
      if (estabaAsignado) asignado.delete(k); else asignado.add(k);
      return { ...x, asignado };
    }));
    axios.post(API_BASE_URL + CHECKLIST_ASIGNACION_TOGGLE, {
      tarea_id: t.id, dia_semana: dia, turno_numero: turno,
    }, { headers: authHeader() })
      .then((res) => { if (!res.data?.success) throw new Error(res.data?.message); })
      .catch((e) => {
        setTareas((prev) => prev.map((x) => {
          if (x.id !== t.id) return x;
          const asignado = new Set(x.asignado);
          if (estabaAsignado) asignado.add(k); else asignado.delete(k);
          return { ...x, asignado };
        }));
        avisarError(e.response?.data?.message || e.message || 'Error al asignar.');
      });
  };

  const clonar = async () => {
    if (!clonarDestino) return;
    setClonando(true);
    try {
      const res = await axios.post(API_BASE_URL + CHECKLIST_TAREA_CLONAR, {
        sucursal_origen_id: sucursalId, sucursal_destino_id: clonarDestino,
      }, { headers: authHeader() });
      if (!res.data?.success) throw new Error(res.data?.message || 'Error al clonar.');
      avisarExito(res.data.message || 'Catálogo clonado.');
      setClonarOpen(false);
      setClonarDestino('');
    } catch (e) {
      avisarError(e.response?.data?.message || e.message || 'Error al clonar.');
    } finally {
      setClonando(false);
    }
  };

  const otrasSucursales = useMemo(() => sucursales.filter((s) => String(s.id) !== String(sucursalId)), [sucursales, sucursalId]);

  return (
    <Container maxWidth={isMobile ? false : 'xl'} sx={{ px: isMobile ? 1 : 3 }}>
      <MenuInicial />
      <Box sx={{ my: 3 }}>
        <TituloPagina
          icon={ChecklistRtl}
          titulo="Checklist de tareas"
          subtitulo="Tareas operativas por día y turno"
          ayuda="Cargá acá las tareas de limpieza/control de la sucursal (ej. Limpiar baños, Descongelar freezers) y marcá en qué día y turno corresponden. El empleado las va a ver y tildar al cerrar el turno — no bloquea el cierre, pero las pendientes se destacan en el mail de resumen."
          accion={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" startIcon={<SyncAlt />} onClick={() => setClonarOpen(true)} disabled={!sucursalId}>
                Clonar a otra sucursal
              </Button>
              <Button variant="contained" startIcon={<Add />} onClick={() => setNuevaOpen(true)} disabled={!sucursalId}>
                Nueva tarea
              </Button>
            </Box>
          }
        />

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <FormControl size="small" sx={{ minWidth: 220, mb: 2 }}>
          <InputLabel>Sucursal</InputLabel>
          <Select label="Sucursal" value={sucursalId} onChange={(e) => setSucursalId(e.target.value)}>
            {sucursales.map((s) => <MenuItem key={s.id} value={s.id}>{s.nombre}</MenuItem>)}
          </Select>
        </FormControl>

        {loading ? (
          <Cargando />
        ) : tareas.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            Todavía no hay tareas cargadas para esta sucursal.
          </Typography>
        ) : (
          <Paper variant="outlined" sx={{ overflowX: 'auto' }}>
            <Table size="small" sx={{ minWidth: 900 }}>
              <TableHead>
                <TableRow>
                  <TableCell rowSpan={2} sx={{ minWidth: 200, verticalAlign: 'bottom' }}>Tarea</TableCell>
                  {DIAS.map((d) => (
                    <TableCell key={d.n} align="center" colSpan={TURNOS.length} sx={{ borderLeft: '1px solid', borderColor: 'divider' }}>
                      {d.label}
                    </TableCell>
                  ))}
                  <TableCell rowSpan={2} align="center" sx={{ verticalAlign: 'bottom' }}>Activo</TableCell>
                  <TableCell rowSpan={2} sx={{ verticalAlign: 'bottom' }} />
                </TableRow>
                <TableRow>
                  {DIAS.map((d) => TURNOS.map((tn, i) => (
                    <TableCell key={`${d.n}-${tn}`} align="center" sx={{ fontSize: 11, color: 'text.secondary', borderLeft: i === 0 ? '1px solid' : undefined, borderColor: 'divider' }}>
                      T{tn}
                    </TableCell>
                  )))}
                </TableRow>
              </TableHead>
              <TableBody>
                {tareas.map((t) => (
                  <TableRow key={t.id} sx={{ opacity: t.activo ? 1 : 0.5 }}>
                    <TableCell>{t.descripcion}</TableCell>
                    {DIAS.map((d) => TURNOS.map((tn, i) => (
                      <TableCell key={`${d.n}-${tn}`} align="center" padding="checkbox" sx={{ borderLeft: i === 0 ? '1px solid' : undefined, borderColor: 'divider' }}>
                        <Checkbox size="small" checked={t.asignado.has(clave(d.n, tn))} onChange={() => toggleCelda(t, d.n, tn)} disabled={!t.activo} />
                      </TableCell>
                    )))}
                    <TableCell align="center"><Switch size="small" checked={t.activo} onChange={() => toggleActivo(t)} /></TableCell>
                    <TableCell><IconButton size="small" color="primary" onClick={() => abrirEditar(t)}><Edit fontSize="small" /></IconButton></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )}
      </Box>

      <Dialog open={nuevaOpen} onClose={() => !guardandoNueva && setNuevaOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Nueva tarea</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth size="small" label="Descripción" autoFocus sx={{ mt: 1 }}
            value={nuevaDescripcion} onChange={(e) => setNuevaDescripcion(e.target.value)}
            helperText="Ej: Limpiar baños, Descongelar freezers, Controlar temperatura de freezers."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNuevaOpen(false)} disabled={guardandoNueva}>Cancelar</Button>
          <Button variant="contained" onClick={crearTarea} disabled={guardandoNueva || !nuevaDescripcion.trim()}>Crear</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!editar} onClose={() => !guardandoEdit && setEditar(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Editar tarea</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth size="small" label="Descripción" sx={{ mt: 1 }}
            value={editDescripcion} onChange={(e) => setEditDescripcion(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditar(null)} disabled={guardandoEdit}>Cancelar</Button>
          <Button variant="contained" onClick={guardarEdicion} disabled={guardandoEdit || !editDescripcion.trim()}>Guardar</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={clonarOpen} onClose={() => !clonando && setClonarOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Clonar a otra sucursal</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Copia las tareas (y sus días/turnos asignados) de esta sucursal a otra. No duplica lo que ya exista allá.
          </Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Sucursal destino</InputLabel>
            <Select label="Sucursal destino" value={clonarDestino} onChange={(e) => setClonarDestino(e.target.value)}>
              {otrasSucursales.map((s) => <MenuItem key={s.id} value={s.id}>{s.nombre}</MenuItem>)}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClonarOpen(false)} disabled={clonando}>Cancelar</Button>
          <Button variant="contained" onClick={clonar} disabled={clonando || !clonarDestino}>Clonar</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default ChecklistTareas;
